// src/components/FormikInput.js

import React, { useEffect, useState } from "react"
import { FormGroup, Label, Input, FormFeedback } from "reactstrap"
import { useDispatch } from "react-redux"

// #region actions
import { getControlEscolarGetCurp } from "../../../store/actions"

// #endregion

// #region selectors
import {
  useControlEscolarCurp,
  useControlEscolarIsLoadingCurp,
} from "store/selectors"
// #endregion

const FormikInputCurp = ({
  label,
  name,
  max = 18,
  type = "text",
  placeholder,
  formik,
  onChange,
  mask, // Opcional: si usas InputMask
  showCharacterCounter = false,
  isLoading,
  ...props
}) => {
  // #region react-redux
  const dispatch = useDispatch()
  // #endregion

  // #region functions

  const handleBlurCurp = event => {
    // Obtiene el nombre del campo y el valor en mayúsculas
    const name = event.target.name
    const curp = event.target.value

    // Solo envía la petición si no hay errores
    if (!formik.errors.hasOwnProperty("Curp")) {
      dispatch(
        getControlEscolarGetCurp({
          nombreSolicitud: "buscacurp",
          curp: formik.values.Curp,
        })
      )

      formik.setFieldValue("id_Estado_Nacimiento", obtenerIdEstado(curp))
      formik.setFieldValue("Sexo", obtenerGeneroPorCURP(curp))
      formik.setFieldValue(
        "Fecha_Nacimiento",
        obtenerFechaNacimientoPorCURP(curp)
      )
      formik.setFieldValue("Password", generarPassword())
      calcularEdad(obtenerFechaNacimientoPorCURP(curp))
    }

    // Re-envia el evento
    formik.handleBlur(event)
  }

  function generarPassword() {
    const longitud = 6 // Puedes ajustar la longitud del password aquí
    const caracteres =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
    let password = ""

    // Garantizar que el password tenga al menos una letra minúscula, una mayúscula y un número
    const minLower = "abcdefghijklmnopqrstuvwxyz"
    const minUpper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
    const minNum = "0123456789"

    // Añadir un carácter obligatorio de cada tipo
    password += minLower.charAt(Math.floor(Math.random() * minLower.length))
    password += minUpper.charAt(Math.floor(Math.random() * minUpper.length))
    password += minNum.charAt(Math.floor(Math.random() * minNum.length))

    // Llenar el resto del password con caracteres aleatorios hasta alcanzar la longitud deseada
    for (let i = password.length; i < longitud; i++) {
      password += caracteres.charAt(
        Math.floor(Math.random() * caracteres.length)
      )
    }

    // Mezclar el password para que los caracteres obligatorios no estén solo al principio
    password = password
      .split("")
      .sort(() => 0.5 - Math.random())
      .join("")

    return password
  }

  const calcularEdad = fechaNacimiento => {
    const hoy = new Date()
    const nacimiento = new Date(fechaNacimiento)
    let edad = hoy.getFullYear() - nacimiento.getFullYear()
    let mes = hoy.getMonth() - nacimiento.getMonth()
    let dia = hoy.getDate() - nacimiento.getDate()

    if (mes < 0 || (mes === 0 && dia < 0)) {
      edad-- // Si no ha llegado el mes de su cumpleaños este año, restar un año a la edad
      mes += 12 // Compensar el mes negativo sumando 12
    }

    if (dia < 0) {
      mes-- // Ajustar los meses si los días son negativos
      // Calcular los días restantes ajustando según el mes anterior
      const diasDelMesAnterior = new Date(
        hoy.getFullYear(),
        hoy.getMonth(),
        0
      ).getDate()
      dia += diasDelMesAnterior
    }

    //setEdad(`${edad} años, ${mes} meses y ${dia} días`);
    setEdad(`${edad} años, ${mes} meses`)
  }

  function obtenerIdEstado(curp) {
    const estadoMap = {
      AS: 1,
      BC: 2,
      BS: 3,
      CC: 4,
      CS: 5,
      CH: 6,
      CL: 7,
      CM: 8,
      DF: 9,
      DG: 10,
      GR: 11,
      GT: 12,
      HG: 13,
      JC: 14,
      MC: 15,
      MN: 16,
      MS: 17,
      NT: 18,
      NL: 19,
      OC: 20,
      PL: 21,
      QT: 22,
      QR: 23,
      SP: 24,
      SL: 25,
      SR: 26,
      TC: 27,
      TS: 28,
      TL: 29,
      VZ: 30,
      YN: 31,
      ZS: 32,
      XX: -1,
    }

    // Extracción de las letras que indican el estado del CURP
    const estadoCurp = curp.slice(11, 13).toUpperCase()

    // Obtener el id del estado basado en las letras del CURP
    const idEstado =
      estadoMap[estadoCurp] !== undefined ? estadoMap[estadoCurp] : -1

    return idEstado
  }

  function obtenerGeneroPorCURP(curp) {
    // El carácter en la posición 10 del CURP indica el género: 'H' para hombre, 'M' para mujer
    const genero = curp.charAt(10).toUpperCase()

    if (genero === "H") {
      return 1 // Hombre
    } else if (genero === "M") {
      return 0 // Mujer
    } else {
      return -1 // Indica que el CURP no tiene un formato correcto para el género
    }
  }

  function obtenerFechaNacimientoPorCURP(curp) {
    // Extracción de la fecha de nacimiento del CURP (posiciones 4 a 9)
    // Año (2 dígitos), mes (2 dígitos), día (2 dígitos)
    const anio = curp.slice(4, 6)
    const mes = curp.slice(6, 8)
    const dia = curp.slice(8, 10)

    // Determinar el siglo completo del año
    const anioCompleto =
      parseInt(anio) +
      (parseInt(anio) >= 0 && parseInt(anio) <= 22 ? 2000 : 1900)

    // Creación de la fecha
    const fechaNacimiento = new Date(`${anioCompleto}-${mes}-${dia}`)

    if (isNaN(fechaNacimiento)) {
      return "Fecha no válida" // En caso de que los datos no formen una fecha válida
    }

    return fechaNacimiento.toISOString().substring(0, 10) // Retorna la fecha en formato YYYY-MM-DD
  }

  // #endregion

  // #region const-selectors
  const curp = useControlEscolarCurp()
  const isLoadingCurp = useControlEscolarIsLoadingCurp()
  // #endregion selectors

  // #region Internal States
  const [edad, setEdad] = useState("")
  // #endregion

  // #region useEffects
  useEffect(() => {
    if (!isLoadingCurp && curp.length > 0) {
      if (curp == formik.values.Curp) {
        formik.setFieldError("Curp", "Curp repetido!")
      }
    }
  }, [isLoadingCurp, curp])

  // #endregion

  return (
    <FormGroup>
      <Label htmlFor={name}>{label}</Label>
      {!isLoading ? (
        <>
          <Input
            type={type}
            name={name}
            id={name}
            placeholder={placeholder}
            value={formik.values[name]}
            onChange={onChange || formik.handleChange}
            onBlur={handleBlurCurp}
            invalid={formik.touched[name] && !!formik.errors[name]}
            maxLength={max} // Limita la entrada a max caracteres
            autoComplete="off"
            {...props}
          />{" "}
          {showCharacterCounter ? (
            <div className="text-end">
              {formik.values[name].length}/{max}
            </div>
          ) : null}
        </>
      ) : (
        <div>
          <p aria-hidden="true" className="mt-2">
            <span className="placeholder col-12 placeholder-lg placeholder-glow"></span>
          </p>
        </div>
      )}
      {formik.touched[name] && formik.errors[name] && (
        <FormFeedback>{formik.errors[name]}</FormFeedback>
      )}
    </FormGroup>
  )
}

export default FormikInputCurp
